import { CSSProperties } from 'react';
import { Box, Divider, IconButton, Tooltip } from '@material-ui/core';
import KeyboardArrowUpRoundedIcon from '@material-ui/icons/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import DeleteOutlineRoundedIcon from '@material-ui/icons/DeleteOutlineRounded';
import RestoreRoundedIcon from '@material-ui/icons/RestoreRounded';
import SwapHorizRoundedIcon from '@material-ui/icons/SwapHorizRounded';

import {
  AIProductMatchBadge,
  SButton,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import { getProductStatus, ImagePreview, ProductLabel } from 'componentsV2';
import { Product } from '@setvi/shared/interfaces';
import { Stock } from 'pages/products/components/stock';
import { getStockInfo } from 'pages/products/components/products-list/useProductColumns/contents/stock-info';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { RfqAlternate } from 'Services/Query/Auto-RFQ/Types';
import { PricingMode } from 'pages/auto-rfq/store/rfqConfig';
import { MatchIndicator, PricingDisplay } from '../index';
import { iconStyle } from '../../styled';
import {
  AltCard,
  AltStatusBadge,
  ControlBtn,
  Eyebrow,
  SlotLabel
} from './styled';

// Reorder icon buttons (↑ ↓) rendered as small outlined squares.
const reorderBtnStyle: CSSProperties = {
  border: '1px solid #E0E0E0',
  borderRadius: 8,
  width: 30,
  height: 30
};

// Labeled action buttons (Reset · Replace · Remove) — icon + text. Compact
// padding; SButton supplies the Setvi pill border-radius.
const actionBtnStyle: CSSProperties = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 600,
  minHeight: 28,
  padding: '2px 12px',
  borderColor: '#E0E0E0',
  color: '#5D7083'
};

const removeBtnStyle: CSSProperties = {
  ...actionBtnStyle,
  color: '#DC2626',
  borderColor: '#F3C9C9'
};

export interface AlternateSlotProps {
  alternate: RfqAlternate;
  // 1 => Alternative 1, 2 => Alternative 2 (fixed slot label).
  slotLabel: 1 | 2;
  // Active grid search term — alternates are searchable (QOT-1738), so the
  // matched substring is highlighted on the card exactly like the Top match.
  search?: string;
  rfqToQuoteEnabled?: boolean;
  pricingMode?: `${PricingMode}`;
  defaultMarginEnabled?: boolean;
  // Tenant catalog config (product.grid.stock.show), passed from the parent so
  // the alternate gates the Stock indicator exactly like the Top match card.
  showStock?: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onReplace: () => void;
  onRemove: () => void;
  onReset: () => void;
  // Opens the "Select Match" dialog with more candidate products for this line
  // item (mirrors the Top card's "View More Matches"). Rendered as the bottom
  // "More Alternatives" link.
  onMore?: () => void;
  // Opens the product details modal for this alternate's product (mirrors the
  // Top match card's clickable product name). Wired only when the alternate
  // resolves to a catalog product (see the productId guard below).
  onProductClick?: () => void;
}

const placeholder = '/images/product-placeholder.svg';

export const AlternateSlot = ({
  alternate,
  slotLabel,
  search = '',
  pricingMode,
  rfqToQuoteEnabled,
  defaultMarginEnabled,
  showStock,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onReplace,
  onRemove,
  onReset,
  onMore,
  onProductClick
}: AlternateSlotProps) => {
  // Removed/empty slot. A slot with no AI original (e.g. a line with too few
  // candidates) has nothing to restore — hide it entirely. Otherwise show a
  // slim row with a restore affordance (the product itself stays hidden).
  if (alternate.state !== 'filled') {
    if (!alternate.aiOriginal) return null;
    return (
      <AltCard style={{ padding: '10px 14px', opacity: 0.8 }}>
        <Eyebrow>
          <SlotLabel>{`Alternative ${slotLabel}`}</SlotLabel>
          <ControlBtn onClick={onReset} title="Restore alternative">
            <span>Restore</span>
          </ControlBtn>
        </Eyebrow>
      </AltCard>
    );
  }

  // A manual pick has no match % or AI reasoning.
  const isManual =
    alternate.source === 'manual' || alternate.percentage == null;

  const inventory = alternate.inventory ?? null;
  const src = alternate.image || placeholder;

  // The product name opens the details modal only when the alternate resolves
  // to a catalog product (manual, typed-in entries have no productId to fetch).
  const canOpenDetails = !!onProductClick && !!alternate.productId;

  return (
    <AltCard>
      <Eyebrow>
        <Box
          display="flex"
          alignItems="center"
          gridGap={6}
          style={{ paddingLeft: 8 }}>
          <SlotLabel>{`Alternative ${slotLabel}`}</SlotLabel>
        </Box>

        {/* Two clusters: reorder (↑ ↓, icon-only) separated from the labeled
            actions (Reset · Replace · Remove) by a divider, so the row reads as
            two groups instead of one undivided blob (QOT-1748). */}
        <Box display="flex" alignItems="center" gridGap={8}>
          <Box display="flex" alignItems="center" gridGap={4}>
            <Tooltip arrow title="Move up">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveUp}
                  onClick={onMoveUp}
                  style={{ ...reorderBtnStyle, opacity: canMoveUp ? 1 : 0.4 }}>
                  <KeyboardArrowUpRoundedIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Move down">
              <span>
                <IconButton
                  size="small"
                  disabled={!canMoveDown}
                  onClick={onMoveDown}
                  style={{
                    ...reorderBtnStyle,
                    opacity: canMoveDown ? 1 : 0.4
                  }}>
                  <KeyboardArrowDownRoundedIcon style={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            style={{ margin: '2px 0' }}
          />

          {/* Labeled actions. Reset uses a distinct restore glyph (not the
              two-arrow icon that looked like Replace) so it reads at a glance
              (QOT-1749); Remove is a trash button in this action group, not a
              corner ✕ (QOT-1750). */}
          <Box display="flex" alignItems="center" gridGap={6}>
            <SButton
              size="small"
              variant="outlined"
              color="default"
              onClick={onReset}
              startIcon={<RestoreRoundedIcon style={{ fontSize: 16 }} />}
              style={actionBtnStyle}>
              Reset
            </SButton>
            <SButton
              size="small"
              variant="outlined"
              color="default"
              onClick={onReplace}
              startIcon={<SwapHorizRoundedIcon style={{ fontSize: 16 }} />}
              style={actionBtnStyle}>
              Replace
            </SButton>
            <SButton
              size="small"
              variant="outlined"
              color="default"
              onClick={onRemove}
              startIcon={<DeleteOutlineRoundedIcon style={{ fontSize: 16 }} />}
              style={removeBtnStyle}>
              Remove
            </SButton>
          </Box>
        </Box>
      </Eyebrow>

      {/* Header row: thumbnail + name / SKU • MFG + status chips + pricing.
          Mirrors the Top match card layout. */}
      <Box display="flex" gridGap={12} style={{ minWidth: 0 }}>
        <Box
          minWidth={96}
          width={96}
          display="flex"
          alignItems="flex-start"
          justifyContent="center">
          <ImagePreview
            title={
              <img
                src={src}
                alt={alternate.name || 'Alternative'}
                onError={e => {
                  e.currentTarget.src = placeholder;
                }}
                style={{
                  width: 180,
                  height: 180,
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  borderRadius: 10,
                  padding: 20
                }}
              />
            }
            placement="right"
            arrow>
            <Box
              width={64}
              height={64}
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                border: '1px solid #E0E0E0',
                borderRadius: 12,
                overflow: 'hidden',
                background: '#fff'
              }}>
              <img
                src={src}
                alt={alternate.name || 'Alternative'}
                onError={e => {
                  e.currentTarget.src = placeholder;
                }}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
          </ImagePreview>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gridGap={4}
          style={{ minWidth: 0, flex: 1 }}>
          <Box display="flex" alignItems="center" gridGap={6}>
            <SHighlightText
              value={toTitleCase(alternate.name || '')}
              searchQuery={search}
              weight="semibold"
              fontColor="#333D47"
              maxLines={1}
              onClick={canOpenDetails ? onProductClick : undefined}
              style={canOpenDetails ? { cursor: 'pointer' } : undefined}
            />
            {alternate.isPrivateLabel && <ProductLabel />}
            {alternate.isPreferredVendor && (
              <ProductLabel type="strategic-vendor" />
            )}
          </Box>

          {/* SKU • MFG + Active / Stocked status chips. */}
          <Box
            display="flex"
            alignItems="center"
            gridGap={4}
            style={{ color: '#5D7083' }}>
            <SHighlightText
              value={`SKU: ${alternate.sku || '-'}`}
              searchQuery={search}
              size="sm"
              fontColor="#5D7083"
              maxLines={1}
            />
            {!!alternate.vendor?.name && (
              <>
                <Box component="span">•</Box>
                <SHighlightText
                  value={`MFG: ${toTitleCase(alternate.vendor.name)} ${
                    alternate.manufacturerSku
                      ? `(${alternate.manufacturerSku})`
                      : ''
                  }`}
                  searchQuery={search}
                  size="sm"
                  fontColor="#5D7083"
                  maxLines={1}
                />
              </>
            )}
            {!!alternate.active && (
              <AltStatusBadge>
                <SText
                  title={getProductStatus(alternate as unknown as Product)}
                />
              </AltStatusBadge>
            )}
            {!!alternate.stockInfo && (
              <AltStatusBadge>
                <SText title={getStockInfo(alternate.stockInfo)} />
              </AltStatusBadge>
            )}
          </Box>

          {/* Sell • Cost • availability — same components/layout AND gating as
              the Top match card: PricingDisplay handles no-pricing itself, and
              Stock is driven by the tenant's stock-display config (showStock),
              not by whether inventory is present — so a catalog alternate shows
              "Out of stock" exactly like the Top would. */}
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              color: '#5D7083'
            }}>
            <PricingDisplay
              cost={alternate.cost ?? undefined}
              sell={alternate.sell ?? undefined}
              search={search}
              rfqToQuoteEnabled={!!rfqToQuoteEnabled}
              pricingMode={pricingMode}
              isManual={isManual}
              showEndDot={showStock}
              defaultMarginEnabled={defaultMarginEnabled}
            />
            {showStock && (
              <Stock
                newDesign
                inventory={inventory}
                isDropship={!!alternate.dropship}
                isStockProduct={alternate.isStockProduct ?? undefined}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* AI Reasoning with a compact match badge + label column (full
          "Close Match"/"Possible Match" text stays visible), separated by
          top/bottom dividers. */}
      {!isManual && !!alternate.reasoning && (
        <Box
          display="flex"
          alignItems="center"
          gridGap={12}
          style={{
            borderTop: '1px solid #DCE1E7',
            borderBottom: '1px solid #DCE1E7',
            padding: '8px 0'
          }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gridGap={4}
            style={{ minWidth: 96, width: 96 }}>
            <AIProductMatchBadge
              value={alternate.percentage || 0}
              fontSize={10}
              padding="2px 8px"
            />
            <MatchIndicator
              percentage={alternate.percentage || 0}
              compact
              fontSize={11}
            />
          </Box>
          <SText
            size="sm"
            fontColor="#333D47"
            maxLines={3}
            lineHeight="20px"
            prefix={
              <b style={{ color: '#20262D', marginRight: 4 }}>AI Reasoning:</b>
            }
            title={alternate.reasoning}
          />
        </Box>
      )}

      {/* Manual / catalog pick has no AI reasoning — show a compact
          "Manual Selection" (catalog) / "Manual Entry" (typed-in) badge + note.
          Uses the alternate's own AltStatusBadge (same size as the Active /
          Stocked chips) rather than the larger Top-match chip. `stockInfo`
          present ⇒ catalog. */}
      {isManual && (
        <Box
          display="flex"
          alignItems="center"
          gridGap={8}
          style={{
            borderTop: '1px solid #DCE1E7',
            borderBottom: '1px solid #DCE1E7',
            padding: '12px 0'
          }}>
          <AltStatusBadge>
            <SText
              title={alternate.stockInfo ? 'Manual Selection' : 'Manual Entry'}
            />
          </AltStatusBadge>
          <SText
            size="sm"
            fontColor="#5D7083"
            maxLines={2}
            lineHeight="20px"
            title={
              alternate.stockInfo
                ? 'Product was added from catalog.'
                : 'Product was added manually.'
            }
          />
        </Box>
      )}

      {/* Opens the Select Match dialog with more candidate products. */}
      {!!onMore && (
        <Box>
          <SButton
            variant="text"
            size="small"
            onClick={onMore}
            style={{ ...iconStyle, fontSize: 12, textTransform: 'none' }}>
            More alternatives
          </SButton>
        </Box>
      )}
    </AltCard>
  );
};

export default AlternateSlot;
